import { useSyncExternalStore } from "react";

/**
 * Whether the browser thinks it has a network.
 *
 * `navigator.onLine` is a weak signal — it reports the link, not reachability,
 * so a captive portal or a dead VPN still reads as online. It is nonetheless the
 * only *instant* signal there is, and the states it does catch (a closed laptop
 * lid, a train tunnel, wifi switched off) are the common ones. Everything that
 * it misses still surfaces the old way, as a failed request.
 *
 * So: treat `false` as certainly offline and `true` as "no reason to think
 * otherwise". Nothing in the app is blocked on this being right — it decides
 * whether to *say* something and whether to bother retrying, never whether the
 * user's work is kept.
 */

function subscribe(onChange: () => void): () => void {
  window.addEventListener("online", onChange);
  window.addEventListener("offline", onChange);
  return () => {
    window.removeEventListener("online", onChange);
    window.removeEventListener("offline", onChange);
  };
}

function getSnapshot(): boolean {
  // Older jsdom and any non-browser render have no navigator.onLine; assume a
  // connection rather than opening with an offline banner nobody asked for.
  return typeof navigator === "undefined" || navigator.onLine !== false;
}

/** Server-render and the first client paint agree: online until told otherwise. */
function getServerSnapshot(): boolean {
  return true;
}

export function useOnlineStatus(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
